<template>
  <div class="flex mb-12 collection-form">
    <div class="w-3/5 content-left-panel">
      <div class="flex flex-wrap justify-end name-field">
        <vs-input
          class="w-full"
          label="Collection name"
          v-model="localCollection.name"
        />
      </div>
      <div class="flex flex-wrap justify-between mt-10">
        <div class="w-full">
          <label class="vs-input--label">Slug</label>
          <vx-input-group
            class="mb-base"
            prependClasses="border border-solid border-grey border-r-0"
          >
            <template slot="prepend">
              <div class="prepend-text">
                <vs-switch
                  v-model="autoAlias"
                  :checked="autoAlias"
                  class="auto-alias-checkbox"
                />
              </div>
            </template>

            <vs-input
              class="auto-alias-field"
              v-model="localCollection.slug"
              :disabled="autoAlias"
            />
          </vx-input-group>
        </div>
      </div>
      <div class="w-full flex flex-wrap">
        <div class="flex w-1/2 px-10 mt-5 mb-10">
          <vs-switch
            v-model="localCollection.is_featured"
            :checked="localCollection.is_featured"
            class="mr-5 is-featured-field"
          />
          <label>Is featured</label>
        </div>
      </div>
      <div class="w-full">
        <vs-textarea
          class="mb-0 md:mt-10 mt-6 bg-white"
          rows="4"
          label="Description"
          v-model="localCollection.description"
        />
      </div>
      <div
        id="collection-async-api-data-2"
        class="flex mt-10 flex-wrap vs-con-loading__container"
      >
        <div class="w-full pb-5">
          <label class="vs-input--label">Categories</label>
          <v-select
            multiple
            v-model="localCollection.categories"
            placeholder="Select one or more categories"
            :reduce="(category) => category.value"
            :options="getCategories"
          />
        </div>
        <div class="w-1/2 pb-5 pr-3">
          <label class="vs-input--label">Difficulty</label>
          <v-select
            v-model="localCollection.difficulty"
            placeholder="Select a level"
            :reduce="(difficulty) => difficulty.value"
            :options="getCollectionDifficulties"
          />
        </div>
        <div class="w-1/2 pb-5 pl-3">
          <label class="vs-input--label">Learning Areas</label>
          <v-select
            multiple
            v-model="localCollection.learning_areas"
            placeholder="Select one or more areas"
            :options="getLAreas"
            :reduce="(la) => la.value"
          />
        </div>
        <div class="w-full pb-5">
          <label class="vs-input--label">Tags</label>
          <v-select
            v-model="localCollection.tags"
            multiple
            taggable
            push-tags
            :closeOnSelect="false"
            placeholder="Select one or more tags"
            :options="tagsOptions"
            :filterable="false"
            @search="onSearchTags"
          >
            <template slot="no-options">type to search or add a tag</template>
          </v-select>
        </div>
        <div class="flex w-full flex-wrap pb-5">
          <label class="vs-input--label">Related Collections</label>
          <v-select
            class="w-full"
            multiple
            v-model="localCollection.related_collections"
            placeholder="Select collections related"
            :options="fullCollectionsOptions"
            :filterable="false"
            @search="onSearchCollections"
            @input="updateCollectionsCache"
            :reduce="(collection) => collection.value"
          >
            <template slot="no-options">type to search a collection</template>
          </v-select>
        </div>
        <div class="flex w-full flex-wrap pb-10">
          <label class="vs-input--label">Featured labs</label>
          <v-select
            class="w-full"
            multiple
            v-model="localCollection.featured_labs"
            placeholder="Select featured labs"
            :options="labsOptions"
            :filterable="false"
            @search="onSearchLabs"
            :reduce="(lab) => lab.value"
          >
            <template slot="no-options">type to search a lab</template>
          </v-select>
        </div>
      </div>
      <vs-alert
        color="danger"
        icon-pack="feather"
        icon="icon-info"
        class="mb-5"
        v-if="
          localCollection.content &&
          !localCollection.content.length &&
          rememberToSave
        "
      >
        You must add at least one Group and Topic although they are not shown in
        the frontend later.
      </vs-alert>
      <div class="flex flex-wrap mt-10 structure-import">
        <div
          class="flex cursor-pointer w-1/4 mb-5 add mt-5 add-grouper"
          @click.stop="
            () => {
              addGroup({ parent: null, index: 0, type: 'group' })
            }
          "
        >
          <feather-icon
            icon="PlusCircleIcon"
            svgClasses="h-5 w-5 color-current text-grey"
            class="flex items-center ml-1 mr-2"
          />
          Add a Group
        </div>
      </div>
      <div
        class="mt-5 tree-structure"
        v-if="localCollection.content && localCollection.content.length"
      >
        <div class="my-10" v-if="importCsvErrors && importCsvErrors.length">
          <ul class="import-csv-errors pl-10">
            <li v-for="([error, video], index) in importCsvErrors" :key="index">
              <span class="text-danger">{{ error }}</span>
              in {{ video }}
            </li>
          </ul>
        </div>
        <vs-alert
          color="warning"
          icon-pack="feather"
          icon="icon-info"
          class="mb-5"
          v-if="rememberToSave"
        >
          Please remember to save the collection after edit the structure.
        </vs-alert>
        <collection-structure
          title="Collection"
          :content="localCollection.content"
          :namespace="'collections'"
          :duration="localCollection.duration_in_seconds"
          :videos="getVideoEntries"
          :quizzes="getQuizEntries"
          :exercises="getExerciseEntries"
          :labs="getLabEntries"
          :iframes="getIFrameEntries"
          :canEditLeafs="true"
          :posibleLeafs="posibleLeafs"
          @delete-structure="deleteCollectionStructure"
          @open-content="openToEdit"
          @add-group="addGroup"
          @add-leaf="addLeaf"
          @delete-group="deleteGroup"
          @delete-leaf="deleteLeaf"
          @rename-group="renameGroup"
        />
      </div>
    </div>
    <div class="w-2/5 pl-10 content-right-panel" sticky-container>
      <div class="flex flex-wrap mb-10" v-if="isEditing">
        <div class="w-full content-status">
          <label class="vs-input--label">Status</label>
          <v-select
            v-model="localCollection.status"
            :clearable="false"
            placeholder="Select one"
            :options="getCollectionPossibleStatus"
            :reduce="(status) => status.value"
          />
        </div>
        <span v-if="isEditing" class="mt-2 text-grey my-ine-url"
          >View this <i>in my.ine</i>:
          <a
            target="_blank"
            :href="`${myIneUrl}/collections/${localCollection.id}`"
            >{{ localCollection.name }}</a
          ></span
        >
      </div>
      <div class="flex flex-wrap mb-10" v-if="isEditing">
        <div
          class="w-full mb-5 published-date"
          v-if="localCollection.published_date"
        >
          <label class="vs-input--label">Published on</label>
          <vs-input
            class="w-full"
            disabled
            :value="localCollection.published_date | dateFormat"
          ></vs-input>
        </div>
        <div class="w-1/2 modified-date">
          <label class="vs-input--label">Modified on</label>
          <vs-input
            disabled
            :value="localCollection.modified | dateFormat"
          ></vs-input>
        </div>
        <div class="w-1/2 pl-1 created-date">
          <label class="vs-input--label">Created on</label>
          <vs-input
            disabled
            :value="localCollection.created | dateFormat"
          ></vs-input>
        </div>
      </div>
      <div
        class="flex action-buttons"
        v-sticky
        sticky-offset="{top: 25}"
        sticky-z-index="999"
      >
        <vs-button
          class="w-full"
          color="primary"
          icon-pack="feather"
          @click="preSave"
          >Save</vs-button
        >
      </div>
      <div
        id="collection-async-api-data-related"
        class="vs-con-loading__container"
      >
        <ContentTable
          :data="associatedLP"
          type="Learning Paths"
          relatedWith="collection"
        />
      </div>
    </div>
    <div class="right-sidebar">
      <edit-leaf
        :leaftype="selectedType"
        :id="selectedId"
        :active="sidebarActive"
        parent-class=".collection-form"
        @change-sidebar="closeSidebar"
        @prevent-close="preventToClose"
        @add-to-structure="addLeafToStructure"
      />
    </div>
  </div>
</template>
<script>
import vSelect from 'vue-select'
import Sticky from 'vue-sticky-directive'
import 'flatpickr/dist/flatpickr.css'
import slug from 'slug-generator'
import cloneDeep from 'lodash.clonedeep'
import { mapGetters, mapActions } from 'vuex'
import validate from 'uuid-validate'
import EditLeaf from './forms/EditLeaf'
import CollectionStructure from './forms/TreeStructure'
import { flatten } from '@/utils/utils.js'
import {
  searchTags,
  searchCollections,
  getBasicData,
  searchLabs,
  warningUnsaved,
} from '@/utils/mixins'
import ContentTable from './components/ContentTable'

export default {
  name: 'CollectionForm',
  mixins: [
    searchTags,
    searchCollections,
    searchLabs,
    getBasicData,
    warningUnsaved,
  ],
  directives: { Sticky },
  components: {
    'v-select': vSelect,
    EditLeaf,
    CollectionStructure,
    ContentTable,
  },
  data() {
    return {
      selectedId: null,
      selectedType: null,
      selectedParent: null,
      localCollection: {},
      currentCategories: [],
      configdateTimePicker: {
        enableTime: true,
        dateFormat: 'm-d-Y H:i',
      },
      autoAlias: true,
      sidebarActive: false,
      importCsvErrors: null,
      rememberToSave: false,
      rememberToSaveFiles: false,
      preventToCloseSidebar: false,
      searchingRelatedOf: {
        loading: false,
        group: null,
      },
      filesLoading: false,
      sidebarReviews: false,
      posibleLeafs: ['lab'],
      myIneUrl: process.env.VUE_APP_MY_INE_URL,
    }
  },
  computed: {
    ...mapGetters('vendors', ['getVendors']),
    ...mapGetters('categories', ['getCategories']),
    ...mapGetters('lareas', ['getLAreas']),
    ...mapGetters('videos', ['getVideoEntries', 'getCurrentVideo']),
    ...mapGetters('quizzes', ['getQuizEntries', 'getCurrentQuiz']),
    ...mapGetters('exercises', ['getExerciseEntries']),
    ...mapGetters('labs', ['getLabEntries']),
    ...mapGetters('iframes', ['getIFrameEntries']),
    ...mapGetters('collections', [
      'getCurrentCollection',
      'getCollectionPossibleStatus',
      'getCollectionDifficulties',
      'getCollectionStructure',
      'getStatusTransitions',
    ]),
    canUploadCSV() {
      return (
        this.isEditing &&
        this.localCollection.content &&
        !this.localCollection.content.length
      )
    },
    isEditing() {
      return this.$route.name === 'edit-collection'
    },
    fullCollectionsOptions() {
      return this.getFullCollectionsOptions()
    },
    associatedLP() {
      if (
        this.localCollection &&
        this.localCollection.learning_paths &&
        this.localCollection.learning_paths.length
      ) {
        return {
          count: this.localCollection.learning_paths.length,
          results: this.localCollection.learning_paths,
        }
      }
      return { count: 0, results: [] }
    },
  },
  methods: {
    ...mapActions('videos', [
      'getAllVideos',
      'saveVideoToEdit',
      'resetCurrentVideo',
    ]),
    ...mapActions('quizzes', [
      'getAllQuizzes',
      'getQuizAction',
      'saveQuizToEdit',
      'resetCurrentQuiz',
    ]),
    ...mapActions('exercises', [
      'getAllExercises',
      'saveExerciseToEdit',
      'resetCurrentExercise',
    ]),
    ...mapActions('labs', [
      'getAllLabs',
      'getLabAction',
      'fetchObjectives',
      'searchLabsById',
      'saveLabToEdit',
      'resetCurrentLab',
      'getAllLabsById',
    ]),
    ...mapActions('iframes', [
      'getAllIFrames',
      'saveIFrameToEdit',
      'resetCurrentIFrame',
    ]),
    ...mapActions('lpaths', ['searchLPaths']),
    ...mapActions('collections', [
      'getCollectionAction',
      'resetCurrentCollection',
      'saveCollectionToApi',
      'saveCollectionToStore',
      'addLeafToContentStructure',
      'addLeafsBetweenContentStructure',
      'removeLeafToContentStructure',
      'addGroupToContentStructure',
      'removeGroupToContentStructure',
      'renameGroupOfContentStructure',
      'deleteContentStructure',
      'searchRelatedContent',
    ]),
    ...mapActions({
      searchCollectionApi: 'collections/searchCollections',
    }),
    preSave() {
      if (
        this.getCurrentCollection.status !== this.localCollection.status &&
        !this.getStatusTransitions[this.localCollection.status].includes(
          this.getCurrentCollection.status
        )
      ) {
        this.$vs.dialog({
          color: 'warning',
          title: `Warning`,
          text: `After saving you won't be able to return to ${this.getCurrentCollection.status} status`,
          accept: () => {
            this.confirmSave()
          },
        })
      } else {
        this.confirmSave()
      }
    },
    confirmSave() {
      this.saveLocalCollection()
    },
    async saveLocalCollection() {
      try {
        this.$vs.loading()

        this.saveCollectionToStore(this.localCollection)

        const response = await this.saveCollectionToApi()

        this.localCollection = cloneDeep(this.getCurrentCollection)
        this.getRelatedCollections()
        this.getFeaturedLabs()

        this.rememberToSave = false

        if (this.$route.name === 'create-collection') {
          this.$router.push({
            name: 'edit-collection',
            params: { id: response.id },
          })
        }

        this.$vs.notify({
          title: 'Success',
          text: 'Collection saved',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'success',
        })
      } catch (error) {
        this.showError(error)
      }
      this.$vs.loading.close()
    },
    getDeepArrayIds(item, type) {
      try {
        let input = JSON.parse(JSON.stringify(item))
        let res = input.map(function f(o) {
          return o.content_type === type
            ? o.uuid
            : o.content && o.content.length
            ? o.content.map(f)
            : []
        })
        res = [...new Set(flatten(res, true))]
        return res
      } catch (error) {
        this.showError(error)
      }
    },
    async getAllCollectionItems() {
      if (this.localCollection.content && this.localCollection.content.length) {
        const allStructureVideos = this.getDeepArrayIds(
          this.localCollection.content,
          'video'
        )
        const allStructureQuizzes = this.getDeepArrayIds(
          this.localCollection.content,
          'quiz'
        )
        const allStructureExercises = this.getDeepArrayIds(
          this.localCollection.content,
          'exercise'
        )
        const allStructureLabs = this.getDeepArrayIds(
          this.localCollection.content,
          'lab'
        )
        const allStructureIFrames = this.getDeepArrayIds(
          this.localCollection.content,
          'iframe'
        )

        if (allStructureQuizzes.length) {
          await this.getAllQuizzes({
            ids: allStructureQuizzes,
            page_size: allStructureQuizzes.length.toString(),
          })
        }
        if (allStructureVideos.length) {
          await this.getAllVideos({
            ids: allStructureVideos,
            page_size: allStructureVideos.length.toString(),
          })
        }
        if (allStructureExercises.length) {
          await this.getAllExercises({
            ids: allStructureExercises,
            page_size: allStructureExercises.length.toString(),
          })
        }
        if (allStructureLabs.length) {
          await this.getAllLabsById({
            ids: allStructureLabs,
            fields: 'base',
          })
        }
        if (allStructureIFrames.length) {
          await this.getAllIFrames({
            ids: allStructureIFrames,
            page_size: allStructureIFrames.length.toString(),
          })
        }
      }
    },
    async openToEdit(item) {
      try {
        switch (item.content_type) {
          case 'video':
            this.saveVideoToEdit(this.getVideoEntries[item.uuid])
            break
          case 'quiz':
            this.$vs.loading()
            await this.getQuizAction({ quizId: item.uuid })
            this.$vs.loading.close()
            break
          case 'lab':
            this.$vs.loading()
            await this.getLabAction({ labId: item.uuid })
            await this.fetchObjectives(item.uuid)
            this.$vs.loading.close()
            break
          case 'iframe':
            this.saveIFrameToEdit(this.getIFrameEntries[item.uuid])
            break
          case 'exercise':
            this.saveExerciseToEdit(this.getExerciseEntries[item.uuid])
            break
        }
      } catch (error) {
        this.showError(error)
        this.$vs.loading.close()
      }

      this.selectedId = item.uuid
      this.selectedType = item.content_type
      this.sidebarActive = true
    },
    closeSidebar(event) {
      if (!event && this.preventToCloseSidebar && !this.confirmLeave()) return
      this.sidebarActive = event
      this.selectedType = null

      this.preventToCloseSidebar = false
    },
    closeReviewsSidebar(event) {
      this.sidebarReviews = event
    },
    // @params: {oldName, newName, type}
    renameGroup(e) {
      this.renameGroupOfContentStructure(e)
      this.rememberToSave = true
    },
    // @params: {parent, index, type}
    addGroup(e) {
      this.addGroupToContentStructure(e)
      this.rememberToSave = true
    },
    // @params: {parent, index}
    deleteGroup(e) {
      this.removeGroupToContentStructure(e)
      this.rememberToSave = true
    },
    addLeaf(e) {
      switch (e.type) {
        case 'video':
          this.resetCurrentVideo()
          break
        case 'quiz':
          this.resetCurrentQuiz()
          break
        case 'lab':
          this.resetCurrentLab()
          break
        case 'iframe':
          this.resetCurrentIFrame()
          break
        case 'exercise':
          this.resetCurrentExercise()
          break
      }
      this.selectedParent = e.parent
      this.selectedId = ''
      this.selectedType = e.type
      this.sidebarActive = true
    },
    addLeafToStructure(event) {
      const { type, item } = event
      // add getCurrentVideo to the selected parent
      this.addLeafToContentStructure({
        parent: this.selectedParent,
        leaf: {
          uuid: item.id,
          content_type: type.toLowerCase(),
        },
      })
      this.rememberToSave = true

      this.selectedId = item.id
      this.sidebarActive = false

      if (type.toLowerCase() === 'video') {
        this.addRelatedContent({
          ids: [item.id],
          selectedParent: this.selectedParent,
        })
      }
    },
    deleteLeaf(e) {
      this.removeLeafToContentStructure(e)
      this.rememberToSave = true
    },
    deleteCollectionStructure() {
      this.deleteContentStructure()
      this.localCollection = cloneDeep(this.getCurrentCollection)
      this.rememberToSave = true
    },
    async getCollectionToEdit(collectionId = '') {
      this.$vs.loading()
      await this.getCollectionAction({ collectionId: collectionId })
      this.localCollection = cloneDeep(this.getCurrentCollection)
      this.getAllCollectionItems()
      this.$vs.loading.close()
    },
    async addRelatedContent(params) {
      this.searchingRelatedOf.loading = true
      this.searchingRelatedOf.group = params.selectedParent
      const response = await this.searchRelatedContent(params)
      this.searchingRelatedOf.loading = false
      Object.entries(response).map(([, items]) => {
        items.map((item) => {
          this.addLeafToContentStructure({
            parent: params.selectedParent,
            leaf: {
              uuid: item.uuid,
              content_type: item.content_type.toLowerCase(),
              related: true,
            },
          })
        })
      })
      await this.getAllCollectionItems()
    },
    preventToClose(event) {
      this.preventToCloseSidebar = event
    },
    validateMainLArea() {
      return !(
        this.localCollection.learning_areas &&
        this.localCollection.learning_areas.includes(
          this.localCollection.main_learning_area
        )
      )
    },
    async getRelatedCollections() {
      try {
        if (
          this.localCollection.related_collections &&
          this.localCollection.related_collections.length
        ) {
          // if first item is an uuid we have to get the collections to show their names,
          // otherwise we should reduce localCollection.related_collections to an array of ids and save collection objects to collectionsOptions
          if (validate(this.localCollection.related_collections[0])) {
            const response = await this.searchCollectionApi({
              ids: this.localCollection.related_collections,
            })
            this.collectionsOptions = response.results.map((v) => ({
              label: v.name,
              value: v.id,
            }))
          } else {
            this.collectionsOptions =
              this.localCollection.related_collections.map((v) => ({
                label: v.name,
                value: v.id,
              }))
            this.localCollection.related_collections =
              this.localCollection.related_collections.map((v) => v.id)
          }
          this.seedCollectionsCacheFromItems(this.localCollection.related_collections)
        }
      } catch (error) {
        this.showError(error)
      }
    },
    async getFeaturedLabs() {
      try {
        if (
          this.localCollection.featured_labs &&
          this.localCollection.featured_labs.length
        ) {
          // if first item is an uuid we have to get the labs to show their names,
          // otherwise we should reduce localCollection.featured_labs to an array of ids and save lab objects to labsOptions
          if (validate(this.localCollection.featured_labs[0])) {
            const response = await this.searchLabsById({
              ids: this.localCollection.featured_labs,
              fields: 'base',
            })
            this.labsOptions = response.results.map((v) => ({
              label: v.name,
              value: v.id,
            }))
          } else {
            this.labsOptions = this.localCollection.featured_labs.map((v) => ({
              label: v.name,
              value: v.id,
            }))
            this.localCollection.featured_labs =
              this.localCollection.featured_labs.map((v) => v.id)
          }
        }
      } catch (error) {
        this.showError(error)
      }
    },
  },
  async mounted() {
    try {
      this.$vs.loading({
        container: '#collection-async-api-data-2',
        scale: 0.6,
      })
      this.$vs.loading({
        container: '#collection-async-api-data-related',
        scale: 0.6,
      })

      // @mixin
      await this.getBasicData()
    } catch (error) {
      this.$vs.loading.close('#collection-async-api-data-2 > .con-vs-loading')
      this.$vs.loading.close(
        '#collection-async-api-data-related > .con-vs-loading'
      )
      this.showError(error)
    }
    this.$vs.loading.close('#collection-async-api-data-2 > .con-vs-loading')
    this.$vs.loading.close(
      '#collection-async-api-data-related > .con-vs-loading'
    )
  },
  async created() {
    if (this.$route && this.$route.params.id) {
      if (this.$route.params.id !== this.localCollection.id) {
        try {
          await this.getCollectionToEdit(this.$route.params.id)
          this.getRelatedCollections()
          this.getFeaturedLabs()
        } catch (error) {
          this.showError(error)
          this.$router.push({ name: 'create-collection' })
        }
        this.$vs.loading.close()
      }
    } else {
      this.resetCurrentCollection()
      this.localCollection = cloneDeep(this.getCurrentCollection)
    }
  },
  watch: {
    'localCollection.name': function (newValue) {
      if (this.autoAlias) {
        this.localCollection.slug = slug(newValue)
      }
    },
    'getCurrentCollection.content': function () {
      this.localCollection.content = this.getCurrentCollection.content
    },
    autoAlias: function (newValue) {
      if (newValue) {
        this.localCollection.slug = slug(this.localCollection.name)
      }
    },
  },
  beforeRouteLeave(to, from, next) {
    if (to.name === 'create-collection') {
      this.resetCurrentCollection()
    }
    if (to.name === 'edit-collection') {
      this.getCollectionToEdit(to.params.id)
    }
    this.localCollection = cloneDeep(this.getCurrentCollection)
    next()
  },
}
</script>
<style lang="scss">
.collection-form {
  .con-img-upload {
    padding: 0;
  }
  .content-left-panel {
    .con-img-upload {
      .img-upload {
        height: 100px;
        width: 100px;
      }
      .text-archive {
        font-size: 0.8em;
      }
    }
  }
  .content-right-panel {
    .con-img-upload {
      display: flex;
      .img-upload {
        height: 80px;
        margin: 0;
      }
    }
  }
  .con-input-upload {
    margin: 0;
    width: 100%;
    height: 80px;
  }
  .content-right-panel {
    .con-input-upload {
      height: 80px;
    }
  }
  .import-csv-errors {
    list-style-type: disc;
  }
  .media-files {
    .prepend-text {
      padding: 0;
      .collection-image {
        width: 53px;
      }
    }
  }
}
.vs-sidebar-position-right {
  max-width: 60%;
  padding: 30px;
  .vs-sidebar--items {
    overflow: auto;
  }
}

.vs-con-loading__container {
  position: relative;
  overflow: unset;
}
</style>
